import AutomationApi from '../../apis/automation';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { errorHandler } from '../../helper/error_handler';
import { isErrorWithMessage } from '../../helper';
import { Node } from '@xyflow/react';
import { RootState } from '../../redux/store';
import { AutomationObject, UpdateAutomationParams, UpdateBuildAutomationParams } from '../../params/redux/automation';
import { resettingAutomationLoader, settingSelectedAutomation } from '../slices/automation';

interface AutomationResponse extends Record<string, unknown> {
    success: 0 | 1;
    data: AutomationObject[];
}

export const fetchAutomationThunk = createAsyncThunk(
    'automation/fetch',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response: AutomationResponse = await AutomationApi.fetchAutomation(state.app.token, client_id) as AutomationResponse;
            if (response.success === 1) {
                if (state.automation.selectedAutomation) {
                    const selected = response.data.find((auto) => auto.id === state.automation.selectedAutomation?.id);
                    dispatch(settingSelectedAutomation(selected));
                }
                return response.data;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingAutomationLoader());
        }
    }
);

export const createAutomationThunk = createAsyncThunk(
    'automation/create',
    async ({ value, client_id }:{value: string, client_id: number}, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await AutomationApi.createAutomation(state.app.token, value, client_id);
            if (response?.success === 1) {
                await dispatch(fetchAutomationThunk(client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingAutomationLoader());
        }
    }
);

export const updateAutomationThunk = createAsyncThunk(
    'automation/update',
    async ({ data, client_id }:{ data: UpdateAutomationParams, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await AutomationApi.updateAutomation(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchAutomationThunk(client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingAutomationLoader());
        }
    }
);

export const updateBuildAutomationThunk = createAsyncThunk(
    'automation/update-build',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const data: UpdateBuildAutomationParams = {
                automation_id: state.automation.selectedAutomation?.id || 0,
                automation_json: state.workflow.nodes.filter((node: Node) => node.type !== 'action')
            };
            const response = await AutomationApi.updateBuildAutomation(state.app.token, data);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingAutomationLoader());
        }
    }
);

export const deleteAutomationThunk = createAsyncThunk(
    'automation/delete',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await AutomationApi.deleteAutomation(state.app.token, state.automation.selectedAutomation?.id || 0);
            if (response?.success === 1) {
                await dispatch(fetchAutomationThunk(client_id)).unwrap();
                dispatch(settingSelectedAutomation(null));
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingAutomationLoader());
        }
    }
);

export const duplicateAutomationThunk = createAsyncThunk(
    'automation/duplicate',
    async ({ automationId, client_id }: {automationId: number, client_id: number}, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await AutomationApi.duplicateAutomation(state.app.token, automationId);
            if (response?.success === 1) {
                await dispatch(fetchAutomationThunk(client_id)).unwrap();
                dispatch(settingSelectedAutomation(null));
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingAutomationLoader());
        }
    }
);
